/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { MakeBridgeContext } from '../../types';
import { createBridgeResource } from '../../bridge-resource';
import type { TBridgeMethod } from '../types';
import { BridgeClientError } from '../../../custom-ui/bridge';
import type { IEnvironment } from 'relay-runtime';
import type { PermissionMethod } from '../../../custom-ui/bridge/methods/permissions/usePermission';
import type {
	EgressSetPayload,
	RemoteSetPayload,
} from '../../../custom-ui/bridge/methods/permissions/types';
import { AdminConsentModal } from './AdminConsentModalWithQueue';
import type { ManifestRemoteMap } from './AdminConsentModalWithQueue';
import { getForgeApp } from './getForgeApp';

// ─── Modal queue resource ─────────────────────────────────────────────────────

let modalInstanceCounter = 0;

type PermissionModalState = {
	isModalOpen: boolean;
	queue: Array<() => void>;
};

/**
 * Per-instance bridge resource that tracks the admin consent modal's open state
 * and a queue of pending callers waiting for the modal slot. Using a bridge
 * resource rather than React state keeps the modal logic stateless from the
 * bridge method's perspective while still being safely scoped to each iframe
 * instance.
 */
const permissionModalResource = createBridgeResource<PermissionModalState>(
	'permissionModal',
	() => ({ isModalOpen: false, queue: [] }),
	() => {},
);

// ─── Context type ─────────────────────────────────────────────────────────────

type PermissionContext = MakeBridgeContext<
	'claimBridgeResource' | 'contextIds' | 'extension' | 'extensionDetails',
	'analytics' | 'overlayController' | 'permissionFetchClient' | 'relayEnvironment'
>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fetchAppData = async (
	relayEnvironment: IEnvironment,
	appId: string,
	contextIds: string[],
) => {
	try {
		return await getForgeApp(relayEnvironment, appId, contextIds);
	} catch (e) {
		if (e instanceof Error) {
			const error = new BridgeClientError(e.message);
			error.stack = e.stack;
			throw error;
		}
		throw new BridgeClientError(typeof e === 'string' ? e : String(e));
	}
};

const trackPermission = (
	analytics: PermissionContext['services']['analytics'],
	appId: string,
	installationId: string,
	action: PermissionMethod,
) => {
	analytics.tracker.trackPermissionAction({ appId, installationId, action });
};

/**
 * Waits until the modal slot is free. If a modal is already open the caller is
 * enqueued and will proceed once all preceding modals are closed.
 */
const waitForModalSlot = (state: PermissionModalState): Promise<void> => {
	if (!state.isModalOpen) {
		state.isModalOpen = true;
		return Promise.resolve();
	}

	return new Promise<void>((resolve) => {
		state.queue.push(() => {
			state.isModalOpen = true;
			resolve();
		});
	});
};

/**
 * Releases the modal slot and lets the next queued caller (if any) proceed.
 */
const releaseModalSlot = (state: PermissionModalState): void => {
	state.isModalOpen = false;
	const next = state.queue.shift();
	if (next) {
		next();
	}
};

// ─── egressSet ────────────────────────────────────────────────────────────────

/**
 * Updates the egress allowlist for the app installation.
 *
 * Performs a dry run first to validate the payload. If the dry run succeeds,
 * the admin consent modal is always shown via `overlayController.show`. The
 * method resolves once the admin approves and the real PUT is applied, or
 * rejects when the admin cancels.
 */
export const egressSet: TBridgeMethod<PermissionContext, EgressSetPayload, unknown> = async (
	{
		services: { permissionFetchClient, analytics, overlayController, relayEnvironment },
		extensionDetails: { appId },
		extension: { installationId },
		contextIds,
		claimBridgeResource,
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'egressSet');

	const modalState = claimBridgeResource(permissionModalResource);
	await waitForModalSlot(modalState);

	let app;
	try {
		app = await fetchAppData(relayEnvironment, appId, contextIds);
	} catch (err) {
		releaseModalSlot(modalState);
		throw err;
	}

	const dryRunResp = await permissionFetchClient.egressSet(payload, true);
	if (!dryRunResp.ok) {
		releaseModalSlot(modalState);
		throw new BridgeClientError('Dry run failed');
	}

	return new Promise((resolve, reject) => {
		const instanceKey = ++modalInstanceCounter;
		overlayController.show(
			<AdminConsentModal
				key={instanceKey}
				appName={app.name}
				domainInfo={{ type: 'EGRESS', payload }}
				termsOfUseLink={app.termsOfService ?? null}
				privacyPolicyLink={app.privacyPolicy ?? null}
				onCancel={() => {
					releaseModalSlot(modalState);
					reject(new BridgeClientError('Setting egress was rejected by admin'));
				}}
				onConfirm={async () => {
					const setResp = await permissionFetchClient.egressSet(payload, false);
					const json = await setResp.json();
					if (!setResp.ok) {
						throw new BridgeClientError(JSON.stringify(json, null, 2));
					}
					releaseModalSlot(modalState);
					resolve(json);
				}}
			/>,
			false,
		);
	});
};

// ─── remoteSet ────────────────────────────────────────────────────────────────

/**
 * Updates the remote endpoint configuration for the app installation.
 *
 * Always shows the admin consent modal to allow the admin to verify the
 * configured endpoint before applying it. The dry run verifies the payload
 * is structurally valid before opening the modal.
 */
export const remoteSet: TBridgeMethod<PermissionContext, RemoteSetPayload, unknown> = async (
	{
		services: { permissionFetchClient, analytics, overlayController, relayEnvironment },
		extensionDetails: { appId },
		extension: { installationId },
		contextIds,
		claimBridgeResource,
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'remoteSet');

	const modalState = claimBridgeResource(permissionModalResource);
	await waitForModalSlot(modalState);

	let app;
	try {
		app = await fetchAppData(relayEnvironment, appId, contextIds);
	} catch (err) {
		releaseModalSlot(modalState);
		throw err;
	}

	const dryRunResp = await permissionFetchClient.remoteSet(payload, true);
	if (!dryRunResp.ok) {
		releaseModalSlot(modalState);
		throw new BridgeClientError('Dry run failed');
	}

	// Build the manifest remote map from the app's extension nodes so the modal
	// can show descriptive names and supported patterns for each remote key.
	const node = (app.installationsByContexts?.edges ?? [])[0]?.node;
	const payloadKeys = new Set(payload.remotes.map((r) => r.key));
	const manifestRemoteMap = (node?.appEnvironmentVersion?.extensions?.nodes ?? [])
		.filter(
			(e) => e && e.extensionData.type === 'core:remote' && payloadKeys.has(e.extensionData.key),
		)
		.reduce((acc, e) => {
			if (e?.extensionData) {
				acc[e.extensionData.key] = e.extensionData.configurable;
			}
			return acc;
		}, {} as ManifestRemoteMap);

	return new Promise((resolve, reject) => {
		const instanceKey = ++modalInstanceCounter;
		overlayController.show(
			<AdminConsentModal
				key={instanceKey}
				appName={app.name}
				domainInfo={{ type: 'REMOTE', payload, manifestRemoteMap }}
				termsOfUseLink={app.termsOfService ?? null}
				privacyPolicyLink={app.privacyPolicy ?? null}
				onCancel={() => {
					releaseModalSlot(modalState);
					reject(new BridgeClientError('Setting remote was rejected by admin'));
				}}
				onConfirm={async () => {
					const setResp = await permissionFetchClient.remoteSet(payload, false);
					if (!setResp.ok) {
						throw new BridgeClientError(setResp.statusText);
					}
					releaseModalSlot(modalState);
					resolve(undefined);
				}}
			/>,
			false,
		);
	});
};
